import {
  createRouter,
  createWebHistory,
  NavigationGuardNext,
  RouteLocation,
  NavigationGuard,
  RouteRecordRaw,
  RouteLocationNormalizedGeneric
} from 'vue-router'

import { onAuthStateChanged, getAuth } from 'firebase/auth'
import firebase from 'firebase/compat/app'

import AuthLayout from '@/pages/Auth/layout.vue'
import AuthLogin from '@/pages/Auth/Login.vue'
import AuthOverview from '@/pages/Auth/Overview.vue'
import Logout from '@/pages/Logout/index.vue'

import Invite from '@/pages/Invite/index.vue'

import Profile from '@/pages/User/index.vue'
import Dashboard from '@/pages/Dashboard/index.vue'

import FeatureLayout from '@/pages/Feature/layout.vue'
import FeatureIndex from '@/pages/Feature/index.vue'
import FeatureEdit from '@/pages/Feature/Edit/index.vue'

import OrgLayout from '@/pages/Organization/layout.vue'
import OrgMembers from '@/pages/Organization/Members/index.vue'
import OrgSettings from '@/pages/Organization/Settings/index.vue'
import OrgBilling from '@/pages/Organization/Billing/index.vue'
import ProjectLayout from '@/pages/Project/layout.vue'
import ProjectHome from '@/pages/Project/index.vue'
import ProjectSettings from '@/pages/Project/Settings/index.vue'
import ProjectTemplates from '@/pages/Project/Templates/index.vue'
import ProjectMembers from '@/pages/Project/Members/index.vue'
import ProjectEdit from '@/pages/Project/Edit/index.vue'
import ProjectCreate from '@/pages/Project/Create/index.vue'

import InvitesList from '@/pages/Invite/invitesList.vue'

import LayersLayout from '@/pages/Layers/layout.vue'
import EmptyLayout from '@/pages/Empty/layout.vue'

import Admin from '@/pages/Admin/index.vue'
import AdminUsers from '@/pages/Admin/Users/index.vue'
import AdminOrganizations from '@/pages/Admin/Organizations/index.vue'
import AdminTemplates from '@/pages/Admin/Templates/index.vue'

import FileViewer from '@/pages/FileViewer/index.vue'

import LayerCreate from '@/pages/Layers/Create/index.vue'
import LayerDetail from '@/pages/Layers/Detail/index.vue'

import Reports from '@/pages/Reports/index.vue'
import { features } from '@/config/features'

const nextPreserveQueryParams: NavigationGuard = (
  to: RouteLocationNormalizedGeneric,
  from: RouteLocationNormalizedGeneric,
  next: NavigationGuardNext
) => {
  const isRedirectionNeeded = Object.keys(from.query).length > 0 && JSON.stringify(to.query) !== JSON.stringify(from.query)
  if (isRedirectionNeeded) {
    next({ path: to.path, query: from.query })
  } else {
    next()
  }
}

const requireAuth: NavigationGuard = (
  to: RouteLocationNormalizedGeneric,
  from: RouteLocationNormalizedGeneric,
  next: NavigationGuardNext
) => {
  onAuthStateChanged(getAuth(), (user: any | null) => {
    if (!user) {
      // next({
      //   path: '/auth/login',
      //   query: from.query
      // })
    } else {
      nextPreserveQueryParams(to, from, next)
    }
  })
}

const logout: NavigationGuard = (
  to: RouteLocation,
  from: RouteLocation,
  next: NavigationGuardNext
) => {
  firebase.auth().signOut().then(() => {
    next()
  })
}

const requireBillingEnabled: NavigationGuard = (
  to: RouteLocationNormalizedGeneric,
  from: RouteLocationNormalizedGeneric,
  next: NavigationGuardNext
) => {
  if (!features.stripe.showBillingPage) {
    // Redirect to organization members page if billing is disabled
    next({ name: 'OrgMembers', params: to.params })
  } else {
    // First check auth, then proceed
    requireAuth(to, from, next)
  }
}

const rts: RouteRecordRaw[] = [
  {
    path: '/auth',
    name: 'Auth',
    component: AuthLayout,
    meta: {
      layout: 'modal',
      showBackgroundUI: false
    },
    children: [
      {
        path: 'login',
        name: 'AuthLogin',
        component: AuthLogin,
        meta: {
          title: 'Login',
          layout: 'modal',
          showBackgroundUI: false
        }
      },
      {
        path: 'overview',
        name: 'AuthOverview',
        component: AuthOverview,
        meta: {
          title: 'Overview',
          layout: 'modal',
          showBackgroundUI: false
        }
      }
    ]
  },
  {
    path: '/invite/:inviteCode',
    name: 'Invite',
    component: Invite,
    meta: {
      title: 'Invite',
      layout: 'modal'
    },
  },
  {
    path: '/invites',
    name: 'Invites',
    component: InvitesList,
    meta: {
      title: 'Invites',
      layout: 'modal'
    }
  },
  {
    path: '/user',
    name: 'User',
    component: Profile,
    beforeEnter: requireAuth,
    meta: {
      title: 'Profile Settings',
    },
  },
  {
    path: '/',
    name: 'Dashboard',
    component: Dashboard,
    beforeEnter: requireAuth,
    meta: {
      title: 'Dashboard',
      layout: 'hide'
    }
  },
  {
    path: '/projects/create',
    name: 'CreateProject',
    component: ProjectCreate,
    beforeEnter: requireAuth,
    meta: {
      title: 'Create New Project',
    }
  },
  {
    path: '/organizations/:org_id/projects/:project_id/features/:featureCode',
    name: 'Feature',
    component: FeatureLayout,
    redirect: {name: 'FeatureIndex'},
    beforeEnter: requireAuth,
    children: [
      {
        path: '',
        name: 'FeatureIndex',
        component: FeatureIndex,
        meta: {
          title: 'Feature',
        }
      },
      {
        path: 'edit',
        name: 'FeatureEdit',
        component: FeatureEdit,
        meta: {
          title: 'Edit Feature',
        }
      }
    ]
  },
  {
    path: '/file-viewer/',
    name: 'FileViewer',
    component: FileViewer,
    beforeEnter: requireAuth,
    meta: {
      title: 'Import Data',
      layout: 'float-big'
    }
  },
  {
    path: '/organizations/:org_id',
    name: 'Organization',
    component: OrgLayout,
    redirect: {name: 'OrgMembers'},
    beforeEnter: requireAuth,
    meta: {
      layout: 'float-big'
    },
    children: [
      {
        path: 'members',
        name: 'OrgMembers',
        component: OrgMembers,
        meta: {
          title: 'Organization Members',
          layout: 'float-big'
        }
      },
      {
        path: 'settings',
        name: 'OrgSettings',
        component: OrgSettings,
        meta: {
          title: 'Organization Settings',
          layout: 'float-big'
        }
      },
      {
        path: 'billing',
        name: 'OrgBilling',
        component: OrgBilling,
        beforeEnter: requireBillingEnabled,
        meta: {
          title: 'Organization Billing',
          layout: 'float-big'
        }
      },
    ]
  },
  {
    path: '/organizations/:org_id/projects',
    name: 'OrgProjects',
    component: EmptyLayout,
    beforeEnter: requireAuth,
    meta: {
      title: 'Projects',
    }
  },
  {
    path: '/organizations/:org_id/layers',
    name: 'OrgLayers',
    component: EmptyLayout,
    beforeEnter: requireAuth,
    meta: {
      title: 'Layers',
    }
  },
  {
    path: '/organizations/:org_id/reports',
    name: 'OrgReports',
    component: EmptyLayout,
    beforeEnter: requireAuth,
    meta: {
      title: 'Reports',
    }
  },
  {
    path: '/organizations/:org_id/reports/:session_code',
    name: 'ChatSession',
    component: Reports,
    beforeEnter: requireAuth,
    meta: {
      title: 'Report',
      layout: 'float-big'
    }
  },
  {
    path: '/organizations/:org_id/reports/:reportCode',
    name: 'ReportDetail',
    component: () => import('@/pages/Reports/Detail.vue'),
    beforeEnter: requireAuth,
    meta: {
      title: 'Report Detail',
      layout: 'float-big'
    }
  },
  {
    path: '/organizations/:org_id/layers/create',
    name: 'LayerCreate',
    component: LayerCreate,
    beforeEnter: requireAuth,
    meta: {
      title: 'New Layer',
    }
  },
  {
    path: '/organizations/:org_id/layers/:layer_id',
    name: 'LayerDetail',
    component: LayerDetail,
    beforeEnter: requireAuth,
    meta: {
      title: 'Layer Detail',
    }
  },
  {
    path: '/organizations/:org_id/projects/:project_id',
    name: 'Project',
    component: ProjectLayout,
    redirect: {name: 'ProjectHome'},
    beforeEnter: requireAuth,
    children: [
      {
        path: '',
        name: 'ProjectHome',
        component: ProjectHome,
        meta: {
          title: 'Feed',
        }
      },
      {
        path: 'edit',
        name: 'ProjectEdit',
        component: ProjectEdit,
        meta: {
          title: 'Edit Project',
        }
      },
      {
        path: 'create',
        name: 'ProjectCreate',
        component: ProjectCreate,
        meta: {
          title: 'New Project',
        }
      },
      {
        path: 'settings',
        name: 'ProjectSettings',
        component: ProjectSettings,
        meta: {
          layout: 'float-big',
          title: 'Project Settings',
        }
      },
      {
        path: 'templates',
        name: 'ProjectTemplates',
        component: ProjectTemplates,
        meta: {
          layout: 'float-big',
          title: 'Project Templates',
        }
      },
      {
        path: 'members',
        name: 'ProjectMembers',
        component: ProjectMembers,
        meta: {
          layout: 'float-big',
          title: 'Project Members',
        }
      }
    ]
  },
  {
    path: '/admin/',
    name: 'Admin',
    component: Admin,
    beforeEnter: requireAuth,
    meta: {
      title: 'Admin Tools',
      layout: 'float-big'
    }
  },
  {
    path: '/admin/users',
    name: 'AdminUsers',
    component: AdminUsers,
    beforeEnter: requireAuth,
    meta: {
      title: 'Admin - Members',
      layout: 'full'
    }
  },
  {
    path: '/admin/organizations',
    name: 'AdminOrganizations',
    component: AdminOrganizations,
    beforeEnter: requireAuth,
    meta: {
      title: 'Admin - Organizations',
      layout: 'full'
    }
  },
  {
    path: '/admin/templates',
    name: 'AdminTemplates',
    component: AdminTemplates,
    beforeEnter: requireAuth,
    meta: {
      title: 'Admin - Templates',
      layout: 'full'
    }
  },
  {
    path: '/logout',
    name: 'Logout',
    component: Logout,
    meta: {
      title: 'Logout'
    }
  },
  {
    path: '/:catchAll(.*)',
    redirect: '/',
    beforeEnter: requireAuth
  }
]

const baseUrl = window.location.pathname + window.location.search
let history = null
if (window.location.search.includes('oobCode')) {
  history = createWebHistory(baseUrl)
} else {
  history = createWebHistory()
}

export const router = createRouter({
  history: history,
  routes: rts
})

export default router
